import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth, getStoredAuth } from '../contexts/CustomerAuthContext';
import { usePwaNav } from '../contexts/PwaNavContext';
import { SkeletonBlock } from './Skeleton';
import UsernamePrompt from './UsernamePrompt';
import CustomerUserMenu from './CustomerUserMenu';
import { usePassportData } from '../hooks/usePassportData';
import { useFriendsData } from '../hooks/useFriendsData';
import { useChallengesData } from '../hooks/useChallengesData';
import { useHistoryData } from '../hooks/useHistoryData';
import PassportView from './passport/PassportView';
import PassportCover from './passport/PassportCover';
import FriendsView from './passport/FriendsView';
import ChallengesView from './passport/ChallengesView';
import HistoryView from './passport/HistoryView';
import type { FoodPassportPanelProps, PassportTab } from '../types/passport';
import './FoodPassportPanel.css';

function FoodPassportPanel({ onBackToOrder, onShowAuth, justLoggedIn, standalone: _standalone }: FoodPassportPanelProps) {
  const { t } = useTranslation();
  const { navData } = usePwaNav();
  const { customer, token, logout } = useCustomerAuth();
  const [localAuth] = useState(() => {
    if (customer && token) return null;
    return getStoredAuth();
  });

  const effectiveCustomer = customer ?? justLoggedIn?.customer ?? localAuth?.customer ?? null;
  const effectiveToken = token ?? justLoggedIn?.token ?? localAuth?.token ?? null;

  const [passportTab, setPassportTab] = useState<PassportTab>(() => {
    // Check navData (PWA standalone mode) first, then URL params (browser mode)
    try {
      const tab = navData?.tab || new URLSearchParams(window.location.search).get('tab');
      if (tab === 'challenges' || tab === 'history') return tab;
    } catch { /* */ }
    return 'passport';
  });

  // Data hooks
  const passport = usePassportData({
    effectiveToken,
    effectiveCustomer,
    justLoggedIn,
    onLogout: logout,
    onShowAuth,
  });

  const friends = useFriendsData({ effectiveToken, passportTab });

  const challenges = useChallengesData({
    effectiveToken,
    effectiveCustomerId: effectiveCustomer?.id,
    passportTab,
  });

  const history = useHistoryData({ effectiveToken, passportTab });

  /* ── Preview (not authenticated) ── */
  if (!effectiveCustomer) {
    return (
      <div className="pp-container">
        <div className="pp-ambient" />
        <div className="pp-header">
          <button type="button" className="pp-back-btn" onClick={onBackToOrder} aria-label="Back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
        </div>
        <div className="pp-guest">
          <div className="pp-guest-emblem" aria-hidden="true">
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="14" y="8" width="36" height="48" rx="5" fill="url(#pp-emblem-grad)" />
              <rect x="14" y="8" width="36" height="48" rx="5" stroke="rgba(255,220,180,0.18)" strokeWidth="0.8" />
              <circle cx="32" cy="26" r="6.5" fill="#1A0F09" />
              <path d="M22 44h20M22 48.5h14" stroke="#1A0F09" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
              <path d="M32 18.5l1.4 3 3.3.4-2.4 2.2.6 3.3-2.9-1.6-2.9 1.6.6-3.3-2.4-2.2 3.3-.4z" fill="#FFD7A1" />
              <defs>
                <linearGradient id="pp-emblem-grad" x1="14" y1="8" x2="50" y2="56" gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#FF8A00" />
                  <stop offset="1" stopColor="#C84B0A" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <h1 className="pp-guest-title">{t('foodPassport.title', 'Фуд-паспорт')}</h1>
          <p className="pp-guest-sub">{t('foodPassport.guestTagline', 'Копите штампы за заказы и обменивайте их на подарки')}</p>

          <ul className="pp-guest-perks">
            <li>
              <span className="pp-guest-perk-dot" aria-hidden>✦</span>
              <div>
                <div className="pp-guest-perk-title">{t('foodPassport.perkStampsTitle', 'Штампы и уровни')}</div>
                <div className="pp-guest-perk-sub">{t('foodPassport.perkStampsSub', 'Каждый заказ — штамп. Чем больше штампов, тем выше уровень.')}</div>
              </div>
            </li>
            <li>
              <span className="pp-guest-perk-dot" aria-hidden>★</span>
              <div>
                <div className="pp-guest-perk-title">{t('foodPassport.perkPointsTitle', 'Бонусы за заказ')}</div>
                <div className="pp-guest-perk-sub">{t('foodPassport.perkPointsSub', 'Накапливайте баллы и оплачивайте ими часть следующего заказа.')}</div>
              </div>
            </li>
            <li>
              <span className="pp-guest-perk-dot" aria-hidden>♥</span>
              <div>
                <div className="pp-guest-perk-title">{t('foodPassport.perkFriendsTitle', 'Друзья и подарки')}</div>
                <div className="pp-guest-perk-sub">{t('foodPassport.perkFriendsSub', 'Дарите блюда друзьям и присоединяйтесь к челленджам.')}</div>
              </div>
            </li>
          </ul>

          <button type="button" className="pp-guest-cta" onClick={onShowAuth}>
            {t('foodPassport.ctaButton', 'Войти и завести паспорт')}
          </button>
          <div className="pp-guest-fineprint">{t('foodPassport.guestFineprint', 'Бесплатно. Никакого спама.')}</div>
        </div>
      </div>
    );
  }

  /* ── Loading ── */
  if (passport.loading) {
    return (
      <div className="pp-container">
        <div className="pp-ambient" />
        <div className="pp-header">
          <SkeletonBlock width="120px" height="34px" radius="10px" />
        </div>
        <SkeletonBlock width="100%" height="190px" radius="20px" style={{ margin: '0 0 16px' }} />
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', padding: '0 16px' }}>
          {[1, 2, 3].map(i => <SkeletonBlock key={i} width="80px" height="60px" radius="14px" />)}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '20px 16px 0' }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonBlock key={i} width="56px" height="56px" radius="50%" />
          ))}
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (passport.error) {
    return (
      <div className="pp-container">
        <div className="pp-ambient" />
        <div className="pp-header">
          <button type="button" className="pp-back-btn" onClick={onBackToOrder} aria-label="Back">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          </button>
        </div>
        <div className="pp-error">{passport.error}</div>
      </div>
    );
  }

  /* ── Data loaded ── */
  return (
    <div className="pp-container">
      <div className="pp-ambient" />

      {passport.showUsernamePrompt && (
        <UsernamePrompt onDone={() => passport.setShowUsernamePrompt(false)} />
      )}

      {/* ── HEADER ── */}
      <div className="pp-header">
        <button type="button" className="pp-back-btn" onClick={onBackToOrder} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
        </button>
        <CustomerUserMenu />
      </div>

      {/* ── PASSPORT COVER (always visible above tabs) ── */}
      {passport.data && (() => {
        const restaurants = passport.data.restaurants;
        const holderName = effectiveCustomer.name || effectiveCustomer.email || t('foodPassport.guest');
        const bestRestaurantName = restaurants.length > 0
          ? restaurants.reduce((best, r) => r.orders > best.orders ? r : best, restaurants[0]).name
          : null;
        const dishCounts = new Map<string, number>();
        for (const r of restaurants) {
          for (const o of r.recentOrders) {
            for (const it of o.items) {
              dishCounts.set(it.name, (dishCounts.get(it.name) || 0) + it.quantity);
            }
          }
        }
        let favoriteDishName: string | null = null;
        let bestCount = 0;
        for (const [name, count] of dishCounts) {
          if (count > bestCount) { bestCount = count; favoriteDishName = name; }
        }
        return (
          <PassportCover
            holderName={holderName}
            totalOrders={passport.data.customer.totalOrders}
            totalPoints={passport.data.customer.loyaltyPoints}
            restaurants={restaurants}
            isFlipped={passport.isFlipped}
            onFlip={() => passport.setIsFlipped(f => !f)}
            bestRestaurantName={bestRestaurantName}
            favoriteDishName={favoriteDishName}
          />
        );
      })()}

      <div className="pp-main-tabs">
        <button
          className={`pp-main-tab ${passportTab === 'passport' ? 'pp-main-tab--active' : ''}`}
          onClick={() => setPassportTab('passport')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>
          {t('passport.tabStamps')}
        </button>
        <button
          className={`pp-main-tab ${passportTab === 'challenges' ? 'pp-main-tab--active' : ''}`}
          onClick={() => setPassportTab('challenges')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>
          {t('challenges.tab')}
          {challenges.pendingChallengeCount > 0 && (
            <span className="pp-tab-badge">{challenges.pendingChallengeCount}</span>
          )}
        </button>
        <button
          className={`pp-main-tab ${passportTab === 'history' ? 'pp-main-tab--active' : ''}`}
          onClick={() => setPassportTab('history')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          {t('passport.tabHistory')}
        </button>
      </div>

      {passportTab === 'history' ? (
        <HistoryView
          historyOrders={history.historyOrders}
          historyLoading={history.historyLoading}
          historyLoadingMore={history.historyLoadingMore}
          historyTotal={history.historyTotal}
          historyPage={history.historyPage}
          fetchHistory={history.fetchHistory}
        />
      ) : passportTab === 'challenges' ? (
        <ChallengesView
          challenges={challenges.challenges}
          challengesLoading={challenges.challengesLoading}
          friendsData={friends.friendsData}
          effectiveCustomerId={effectiveCustomer.id}
          showCreateChallenge={challenges.showCreateChallenge}
          currentPoints={passport.data?.customer.loyaltyPoints || 0}
          onSetShowCreateChallenge={challenges.setShowCreateChallenge}
          onAcceptChallenge={challenges.handleAcceptChallenge}
          onDeclineChallenge={challenges.handleDeclineChallenge}
          onCancelChallenge={challenges.handleCancelChallenge}
          onCreateChallenge={challenges.handleCreateChallenge}
        />
      ) : passportTab === 'friends' ? (
        <FriendsView
          friendsData={friends.friendsData}
          friendsLoading={friends.friendsLoading}
          inviteCopied={friends.inviteCopied}
          effectiveToken={effectiveToken}
          onCopyInvite={friends.handleCopyInvite}
          onAcceptFriend={friends.handleAcceptFriend}
          onDeclineFriend={friends.handleDeclineFriend}
          onUnfriend={friends.handleUnfriend}
          fetchFriends={friends.fetchFriends}
        />
      ) : (
        <PassportView
          data={passport.data!}
          effectiveCustomer={effectiveCustomer}
          revealAchIds={passport.revealAchIds}
          showAchOverlayId={passport.showAchOverlayId}
          flyOutAchIds={passport.flyOutAchIds}
          stampedAchIds={passport.stampedAchIds}
          onOverlayDone={passport.handleOverlayDone}
          selectedStamp={passport.selectedStamp}
          selectedStampColor={passport.selectedStampColor}
          onSelectStamp={(stamp, color) => {
            passport.setSelectedStamp(stamp);
            passport.setSelectedStampColor(color);
          }}
          onCloseStamp={() => passport.setSelectedStamp(null)}
          selectedBadge={passport.selectedBadge}
          badgeStats={passport.badgeStats}
          badgeStatsLoading={passport.badgeStatsLoading}
          onSelectBadge={(badge) => {
            passport.setSelectedBadge(badge);
            passport.fetchBadgeStats(effectiveCustomer.id, badge.restaurantId);
          }}
          onCloseBadge={() => passport.setSelectedBadge(null)}
          fetchBadgeStats={passport.fetchBadgeStats}
        />
      )}
    </div>
  );
}

export default FoodPassportPanel;
